import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { getChargebackPageStats } from "../../../../../api";
import useChargebackStore from "../../../../stores/chargeback";
import moment from "moment";
import { toast } from "react-hot-toast";
import SkeletonLoader from "../../../../components/skeleton-loader";
import { formatCurrency as formatAmount } from "../../../../../components/helper/helper";

const ChargebackStats = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    month_to_date: {
      cb_amount: 0,
      cb_amount_ratio: 0,
      cb_count: 0,
      cb_count_ratio: 0
    },
    last_month: {
      cb_amount: 0,
      cb_amount_ratio: 0,
      cb_count: 0,
      cb_count_ratio: 0
    }
  });

  // Get filters from the store
  const { filters } = useChargebackStore();

  // Initialize with default date range (current month)
  const defaultDateRange = {
    startDate: moment().startOf('month').format("YYYY-MM-DD"),
    endDate: moment().endOf('month').format("YYYY-MM-DD")
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {
          startDate: filters.dateRange?.startDate || defaultDateRange.startDate,
          endDate: filters.dateRange?.endDate || defaultDateRange.endDate,
          orgFilter: filters.orgFilter || ""
        };
        const response = await getChargebackPageStats(params);
        if (response?.status === true && response?.data) {
          setData({
            month_to_date: {
              cb_amount: response.data.month_to_date?.cb_amount || 0,
              cb_amount_ratio:
                response.data.month_to_date?.cb_amount_ratio || 0,
              cb_count: response.data.month_to_date?.cb_count || 0,
              cb_count_ratio: response.data.month_to_date?.cb_count_ratio || 0,
            },
            last_month: {
              cb_amount: response.data.last_month?.cb_amount || 0,
              cb_amount_ratio: response.data.last_month?.cb_amount_ratio || 0,
              cb_count: response.data.last_month?.cb_count || 0,
              cb_count_ratio: response.data.last_month?.cb_count_ratio || 0,
            },
          });
        } else {
          toast.error('Failed to fetch chargeback stats');
          setData({
            month_to_date: {
              cb_amount: 0,
              cb_amount_ratio: 0,
              cb_count: 0,
              cb_count_ratio: 0
            },
            last_month: {
              cb_amount: 0,
              cb_amount_ratio: 0,
              cb_count: 0,
              cb_count_ratio: 0
            }
          });
        }
      } catch (error) {
        toast.error('Error fetching chargeback stats');
        setData({
          month_to_date: {
            cb_amount: 0,
            cb_amount_ratio: 0,
            cb_count: 0,
            cb_count_ratio: 0
          },
          last_month: {
            cb_amount: 0,
            cb_amount_ratio: 0,
            cb_count: 0,
            cb_count_ratio: 0
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  if (loading) {
    return (
      <div className={styles.stats_container}>
        <div className={styles.carousel_wrapper}>
          <div className={styles.carousel_track}>
            {[1, 2].map((i) => (
              <div key={i} className={styles.stats_card}>
                <div className={styles.stats_content}>
                  <SkeletonLoader
                    variant="rectangular"
                    height={14}
                    width={160}
                    className={styles.skeleton_header}
                  />
                  <div className={styles.stats_body}>
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className={styles.stats_item}>
                        <SkeletonLoader variant="rectangular" height={24} width={64} />
                        <SkeletonLoader variant="rectangular" height={12} width={80} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const formatRatio = (ratio) => {
    return typeof ratio === 'number' ? ratio.toFixed(2) : '0.00';
  };

  return (
    <div className={styles.stats_container}>
      <div className={styles.carousel_wrapper}>
        <div className={styles.carousel_track}>
          <div className={styles.stats_card}>
            <div className={styles.stats_content}>
              <div className={styles.stats_header}>
                CURRENT MONTH TO DATE
              </div>
              <div className={styles.stats_body}>
                <div className={styles.stats_item}>
                  <div className={styles.stats_value} title={`$${formatAmount(data?.month_to_date?.cb_amount)}`}>$ {formatAmount(data?.month_to_date?.cb_amount)}</div>
                  <div className={styles.stats_label}>Dollar Amount</div>
                </div>
                <div className={`${styles.stats_item} ${styles.align_right}`}>
                  <div className={styles.stats_value} title={`${formatRatio(data?.month_to_date?.cb_amount_ratio)}%`}>{formatRatio(data?.month_to_date?.cb_amount_ratio)}%</div>
                  <div className={styles.stats_label}>Ratio</div>
                </div>
                <div className={styles.divider} />
                <div className={styles.stats_item}>
                  <div className={styles.stats_value} title={`${data?.month_to_date?.cb_count || 0}`}>{data?.month_to_date?.cb_count || 0}</div>
                  <div className={styles.stats_label}>Chargeback Count</div>
                </div>
                <div className={`${styles.stats_item} ${styles.align_right}`}>
                  <div className={styles.stats_value} title={`${formatRatio(data?.month_to_date?.cb_count_ratio)}%`}>{formatRatio(data?.month_to_date?.cb_count_ratio)}%</div>
                  <div className={styles.stats_label}>Ratio</div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stats_card}>
            <div className={styles.stats_content}>
              <div className={styles.stats_header}>
                LAST MONTH
              </div>
              <div className={styles.stats_body}>
                <div className={styles.stats_item}>
                  <div className={styles.stats_value} title={`$${formatAmount(data?.last_month?.cb_amount)}`}>$ {formatAmount(data?.last_month?.cb_amount)}</div>
                  <div className={styles.stats_label}>Dollar Amount</div>
                </div>
                <div className={`${styles.stats_item} ${styles.align_right}`}>
                  <div className={styles.stats_value} title={`${formatRatio(data?.last_month?.cb_amount_ratio)}%`}>{formatRatio(data?.last_month?.cb_amount_ratio)}%</div>
                  <div className={styles.stats_label}>Ratio</div>
                </div>
                <div className={styles.divider} />
                <div className={styles.stats_item}>
                  <div className={styles.stats_value} title={`${data?.last_month?.cb_count || 0}`}>{data?.last_month?.cb_count || 0}</div>
                  <div className={styles.stats_label}>Chargeback Count</div>
                </div>
                <div className={`${styles.stats_item} ${styles.align_right}`}>
                  <div className={styles.stats_value} title={`${formatRatio(data?.last_month?.cb_count_ratio)}%`}>{formatRatio(data?.last_month?.cb_count_ratio)}%</div>
                  <div className={styles.stats_label}>Ratio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargebackStats; 